import {Injectable} from '@angular/core';
import {DomSanitizer} from "@angular/platform-browser";
import {BehaviorSubject, Observable} from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class UtilService {
  pageLoading!: Observable<boolean>;
  pageLoadingSubject!: BehaviorSubject<boolean>;

  constructor(private sanitizer: DomSanitizer) {
    this.pageLoadingSubject = new BehaviorSubject<boolean>(false);
    this.pageLoading = this.pageLoadingSubject.asObservable();
  }

  enablePageLoading() {
    this.pageLoadingSubject.next(true);
    return true;
  }

  disablePageLoading() {
    this.pageLoadingSubject.next(false);
    return false;
  }

  getFirstDayOfWeek() {
    const today = new Date();
    const firstDay = new Date(today.setDate(today.getDate() - today.getDay() + 1));
    return (firstDay.getMonth() + 1) + '/' + firstDay.getDate();
  }

  detectDevice() {
    const userAgent = navigator.userAgent.toLowerCase();
    console.log('User Agent:', userAgent);
    return /android/.test(userAgent) && /mobile/.test(userAgent);
  }

  sanitizeURL(url: string) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  sortByText(a: string, b: string) {
    const nameA = a.toUpperCase();
    const nameB = b.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  }

  getFlowCodeByLocation(): string {
    const path = window.location.pathname;
    const match = path.match(/ventas\/([^\/]+)/);
    return match ? match[1] : '';
  }

  getCurrentUrl(): string {
    return window.location.href;
  }

  /**
   * Formatea un número telefónico según una máscara específica de forma inteligente
   * @param phoneValue El número telefónico a formatear (puede venir en cualquier formato o incompleto)
   * @param mask La máscara a aplicar (donde '9' representa posición para un dígito)
   * @returns El número telefónico formateado según la máscara
   */
  formatPhoneNumber(phoneValue: string, mask: string): string {
    if (!phoneValue || !mask) return phoneValue;

    // Extraer el prefijo de la máscara (caracteres antes del primer '9')
    let prefixMask = '';
    let prefixEndIndex = 0;
    while (prefixEndIndex < mask.length && mask[prefixEndIndex] !== '9') {
      prefixMask += mask[prefixEndIndex];
      prefixEndIndex++;
    }

    // Verificar si el número ya incluye el prefijo (sin recortar espacios)
    const exactPrefixMatch = phoneValue.startsWith(prefixMask) && prefixMask.length > 0;

    // Verificar si incluye el prefijo sin espacios
    const cleanPrefix = prefixMask.replace(/\s/g, '');
    const cleanPhoneStart = phoneValue.substring(0, cleanPrefix.length).replace(/\s/g, '');
    const prefixAlreadyExists = (exactPrefixMatch || cleanPhoneStart === cleanPrefix) && cleanPrefix.length > 0;

    // Extraer los dígitos del teléfono
    let allDigits;
    let result: string;
    if (exactPrefixMatch) {
      // Si coincide exactamente con el prefijo (incluyendo espacios)
      allDigits = phoneValue.substring(prefixMask.length).replace(/\D/g, '');
      result = prefixMask; // Usamos el prefijo exacto de la máscara
    } else if (prefixAlreadyExists) {
      // Si coincide con el prefijo pero sin espacios
      allDigits = phoneValue.substring(cleanPrefix.length).replace(/\D/g, '');
      result = prefixMask; // Usamos el prefijo formateado de la máscara
    } else {
      // Si no tiene el prefijo, extraemos todos los dígitos
      allDigits = phoneValue.replace(/\D/g, '');
      result = ''; // Comenzamos con resultado vacío
    }

    if (!allDigits.length) return prefixAlreadyExists ? prefixMask : '';

    // Construir el resultado según la máscara
    // let result = prefixAlreadyExists ? prefixMask : ''; // Eliminado porque ya se declaró y asignó arriba
    let digitIndex = 0;

    // Si el prefijo ya existe, comenzamos desde después del prefijo en la máscara
    const startIndex = prefixAlreadyExists ? prefixEndIndex : 0;

    for (let i = startIndex; i < mask.length; i++) {
      // Si ya procesamos todos los dígitos disponibles
      if (digitIndex >= allDigits.length) break;

      if (mask[i] === '9') {
        // Posición para un dígito
        result += allDigits[digitIndex];
        digitIndex++;
      } else {
        // Carácter especial (espacio, guion, etc.)
        result += mask[i];
      }
    }

    // Eliminamos caracteres especiales al final si el número está incompleto
    if (result.length > 0) {
      const lastChar = result[result.length - 1];
      if (!/\d/.test(lastChar)) {
        result = result.substring(0, result.length - 1);
      }
    }

    return result;
  }

  /**
   * Convierte un valor numérico de días en su descripción de frecuencia
   * @param days Número de días
   * @param capitalize Indica si la primera letra debe estar en mayúscula (default: true)
   * @returns Descripción de la frecuencia
   */
  getFrequencyDescription(days: number, capitalize: boolean = false, singular: boolean = false): string {
    let result: string;
    switch (days) {
      case 15:
        result = singular ? 'quincenal' : 'quincenales';
        break;
      case 30:
        result = singular ? 'mensual' : 'mensuales';
        break;
      case 7:
        result = singular ? 'semanal' : 'semanales';
        break;
      case 1:
        result = singular ? 'diaria' : 'diario';
        break;
      default:
        result = `cada ${days} días`;
    }

    return capitalize ? result.charAt(0).toUpperCase() + result.slice(1) : result;
  }

  /**
   * Detecta si existe request_id en la URL actual (después del segmento 'solicitud')
   * @returns string | null - El valor del request_id o null si no existe
   */
  getRequestIdFromUrl(): string | null {
    try {
      const pathSegments = window.location.pathname.split('/');

      // Buscar el segmento 'solicitud'
      const solicitudIndex = pathSegments.findIndex(segment => segment === 'solicitud');

      if (solicitudIndex !== -1 && solicitudIndex < pathSegments.length - 1) {
        const requestId = pathSegments[solicitudIndex + 1];
        return requestId;
      }

      return null;
    } catch (error) {
      console.warn('Error al extraer request_id de la URL:', error);
      return null;
    }
  }
}
